import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, imgUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Instagram, MapPin, ArrowLeft, Heart, Sparkles, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

export default function CoupleDetail() {
  const { user_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/directory/couples/${user_id}`);
        setP(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Unable to load profile.");
      }
    })();
  }, [user_id]);

  const adminDelete = async () => {
    if (!p) return;
    if (!confirm(`Delete "${p.display_name}" and all their data permanently? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${user_id}`);
      navigate("/directory", { replace: true });
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
      setDeleting(false);
    }
  };

  const photos = p?.photos || [];
  const prev = useCallback(() => setActiveIdx((i) => (i - 1 + photos.length) % photos.length), [photos.length]);
  const next = useCallback(() => setActiveIdx((i) => (i + 1) % photos.length), [photos.length]);

  useEffect(() => {
    if (photos.length < 2) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, photos.length]);

  if (err) {
    return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <h2 className="font-serif text-3xl mb-3">Hmm.</h2>
      <p className="text-muted mb-6">{err}</p>
      <Link to="/directory" className="qld-btn-primary">Back to directory</Link>
    </div></Layout>;
  }
  if (!p) {
    return <Layout><div className="max-w-3xl mx-auto px-6 py-24 text-center text-muted">Loading…</div></Layout>;
  }

  const main = photos[activeIdx];
  const location = [p.location_city, p.location_state, p.location_country].filter(Boolean).join(", ");
  const collabs = [];
  if (p.open_to_styled_shoots) collabs.push("Styled shoots");
  if (p.open_to_editorial) collabs.push("Editorial");
  if (p.open_to_content_days) collabs.push("Content days");

  const firstName = (p.display_name || "this couple").split(" ")[0];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <Link to="/directory" className="inline-flex items-center gap-2 text-muted hover:text-ink text-sm" data-testid="back-to-directory">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/> Back to directory
          </Link>
          {isAdmin && (
            <button
              onClick={adminDelete}
              disabled={deleting}
              data-testid="admin-delete-profile-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 border-black bg-linen text-wine hover:bg-wine hover:text-white transition text-sm font-medium"
            >
              <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
              {deleting ? "Deleting…" : "Delete this profile"}
            </button>
          )}
        </div>

        {/* Gallery — portrait-friendly hero + thumb strip */}
        <div className="grid md:grid-cols-12 gap-3 md:gap-4 mb-12">
          <div className="md:col-span-8 relative rounded-3xl overflow-hidden border-2 border-black aspect-[3/4] sm:aspect-[4/5] md:aspect-[4/5] bg-powder" data-testid="couple-hero">
            {main ? (
              <img src={imgUrl(main)} alt={p.display_name} className="w-full h-full object-cover"/>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-wine">
                <Heart className="w-16 h-16" strokeWidth={1}/>
              </div>
            )}
            {photos.length > 1 && (
              <>
                <button
                  onClick={prev}
                  aria-label="Previous photo"
                  data-testid="gallery-prev"
                  className="absolute top-1/2 left-3 -translate-y-1/2 w-10 h-10 rounded-full bg-linen border-2 border-black flex items-center justify-center hover:bg-mustard transition shadow-md"
                >
                  <ChevronLeft className="w-5 h-5" strokeWidth={2}/>
                </button>
                <button
                  onClick={next}
                  aria-label="Next photo"
                  data-testid="gallery-next"
                  className="absolute top-1/2 right-3 -translate-y-1/2 w-10 h-10 rounded-full bg-linen border-2 border-black flex items-center justify-center hover:bg-mustard transition shadow-md"
                >
                  <ChevronRight className="w-5 h-5" strokeWidth={2}/>
                </button>
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-linen border-2 border-black rounded-full px-3 py-1 text-xs font-medium" data-testid="gallery-counter">
                  {activeIdx + 1} / {photos.length}
                </div>
              </>
            )}
          </div>
          <div className="md:col-span-4 grid grid-cols-4 md:grid-cols-2 gap-3 md:gap-4 content-start" data-testid="gallery-thumbs">
            {photos.slice(0, 8).map((ph, i) => (
              <button key={ph.id || i} onClick={()=>setActiveIdx(i)} data-testid={`gallery-thumb-${i}`}
                className={`relative rounded-2xl overflow-hidden aspect-[3/4] border-2 transition-all ${i===activeIdx?"border-wine ring-2 ring-wine/30":"border-black hover:opacity-80"}`}>
                <img src={imgUrl(ph)} alt="" className="w-full h-full object-cover"/>
              </button>
            ))}
          </div>
        </div>

        {/* Bio + sidebar */}
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-7">
            <p className="qld-overline mb-3">
              Lovers{p.pronouns && ` · ${p.pronouns}`}{p.partner_pronouns && ` · ${p.partner_pronouns}`}
            </p>
            <h1 className="font-serif text-5xl md:text-6xl text-ink leading-[0.95] mb-6">
              {p.display_name}
            </h1>
            {location && (
              <p className="flex items-center gap-2 text-lg text-muted mb-6">
                <MapPin className="w-4 h-4" strokeWidth={1.5}/> {location}
                {p.travel_radius && <span className="text-sm">· willing to travel {p.travel_radius}</span>}
              </p>
            )}

            <div className="flex flex-wrap gap-2 mb-8">
              {p.identity_tags?.map((i) => <span key={i} className="qld-chip qld-chip-sage">{i}</span>)}
              {p.relationship_type && <span className="qld-chip qld-chip-sky">{p.relationship_type}</span>}
              {p.age_range && <span className="qld-chip qld-chip-butter">{p.age_range}</span>}
            </div>

            <h2 className="font-serif text-2xl text-ink mb-3">About</h2>
            <p className="text-ink/90 leading-relaxed whitespace-pre-wrap text-lg">
              {p.about_text || <span className="italic text-muted">No story added yet.</span>}
            </p>

            {p.cultural_background && (
              <>
                <h3 className="font-serif text-xl text-ink mt-8 mb-2">Cultural background</h3>
                <p className="text-ink/85">{p.cultural_background}</p>
              </>
            )}

            {p.additional_lovers && (
              <>
                <h3 className="font-serif text-xl text-ink mt-8 mb-2">The whole household</h3>
                <p className="text-ink/85 whitespace-pre-wrap" data-testid="detail-additional-lovers">{p.additional_lovers}</p>
              </>
            )}
            {p.body_description && (
              <>
                <h3 className="font-serif text-xl text-ink mt-8 mb-2">In their own words</h3>
                <p className="text-ink/85">{p.body_description}</p>
              </>
            )}
            {p.accessibility_notes && (
              <>
                <h3 className="font-serif text-xl text-ink mt-8 mb-2">Accessibility notes</h3>
                <p className="text-ink/85">{p.accessibility_notes}</p>
              </>
            )}

            {collabs.length > 0 && (
              <>
                <h2 className="font-serif text-2xl text-ink mt-10 mb-3">Open to</h2>
                <div className="flex flex-wrap gap-2">
                  {collabs.map((o) => <span key={o} className="qld-chip qld-chip-butter">{o}</span>)}
                </div>
              </>
            )}

            {p.languages?.length > 0 && (
              <>
                <h2 className="font-serif text-2xl text-ink mt-10 mb-3">Languages</h2>
                <p className="text-ink/90">{p.languages.join(" · ")}</p>
              </>
            )}
          </div>

          {/* Contact sidebar */}
          <aside className="md:col-span-5">
            <div className="bg-lilac border-2 border-black rounded-[1.5rem] p-8 sticky top-24">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-ink" strokeWidth={1.5}/>
                <p className="qld-overline">Want to collaborate?</p>
              </div>
              <h3 className="font-serif text-3xl text-ink mb-5 leading-tight">
                Reach out to <span style={{ fontFamily: "'Bringbold', Georgia, serif" }}>{firstName}</span> directly.
              </h3>
              <p className="text-black/80 text-sm mb-6">
                We don't start the conversations, just make the introduction. You can DM, email or follow these lovers on socials. Always be respectful, be specific.
              </p>
              <div className="space-y-3">
                {p.contact_email && (
                  <a data-testid="contact-email-link" href={`mailto:${p.contact_email}?subject=Love%2C%20In%20Full%20%E2%80%94%20collaboration`}
                     className="qld-btn-primary w-full">
                    <Mail className="w-4 h-4" strokeWidth={1.5}/> Email {firstName}
                  </a>
                )}
                {p.instagram && (
                  <a data-testid="contact-instagram-link" href={`https://instagram.com/${p.instagram.replace(/^@/,"")}`} target="_blank" rel="noreferrer"
                     className="lif-btn lif-btn-mustard w-full">
                    <Instagram className="w-4 h-4" strokeWidth={1.5}/> @{p.instagram.replace(/^@/,"")}
                  </a>
                )}
              </div>
              <p className="mt-6 text-xs text-black/70 leading-relaxed">
                By contacting lovers you agree to the Love, In Full community standards. Only email for real opportunities. No harassment, no trying to promote your services in sales emails. If you're found to have breached these community standards, you'll be removed from our space.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}
